import { Component, OnInit } from "@angular/core";
import { LookupService } from "../../services/technical-letters/lookup.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TechnicalLetterService } from "../../services/technical-letters/technical-letter.service";
import { forkJoin } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { InputDialogComponent } from "../input-dialog/input-dialog.component";
import { LoggedUserService } from "../../services/common/logged-user.service";
import { Router } from "@angular/router";

@Component({
    selector: "app-add-letter",
    templateUrl: "./add-letter.component.html",
    standalone: false
})
export class AddLetterComponent implements OnInit {
  isLoading: boolean = false;
  isAdding: boolean = false;
  issuers: any[] = [];
  statuses: any[] = [];
  categories: any[] = [];
  letterForm: FormGroup;
  validationErrors: string[] = [];

  addLetterFormData = new FormData();

  constructor(
    private lookupService: LookupService,
    private technicalLetterService: TechnicalLetterService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private router: Router,
    loggedUserService: LoggedUserService
  ) {
    loggedUserService.getLoggedUserData().subscribe({
      next: (data) => {
        if (data) {
          this.addLetterFormData.append("RowCreatedBy", data.email);
          this.addLetterFormData.append(
            "RowCreatedDate",
            new Date(new Date().toUTCString()).toISOString().split("T")[0]
          );
        }
      },
    });
    this.letterForm = this.fb.group({
      issuer: [""],
      letterStatus: [""],
      letterCategory: [""],
      letterIssueDate: [null],
      letterRecurringFlag: [false],
      letterTitle: [""],
      letterNumber: [""],
      ict: [false],
      file: [null],
    });
  }

  ngOnInit(): void {
    this.loadAllData();
  }

  private loadAllData(): void {
    this.isLoading = true;

    forkJoin({
      issuers: this.lookupService.getLookups(3),
      statuses: this.technicalLetterService.getAllStatuses(),
      categories: this.technicalLetterService.getAllCategories(),
    }).subscribe({
      next: (data) => {
        this.issuers = data.issuers;
        this.statuses = data.statuses;
        this.categories = data.categories;
        this.categories.push(-1);
        this.statuses.push(-1);
        if (this.issuers && this.issuers.length > 0) {
          this.letterForm.patchValue({
            issuer: this.issuers[0].lookupValue,
          });
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error("Error fetching data:", error);
        this.isLoading = false;
      },
    });
  }

  onIssuerChange(event) {
    const selectedIssuer = event.target.value;
    if (selectedIssuer) {
      forkJoin({
        statuses: this.technicalLetterService.getAllStatuses(selectedIssuer),
        categories:
          this.technicalLetterService.getAllCategories(selectedIssuer),
      }).subscribe({
        next: ({ statuses, categories }) => {
          this.statuses = statuses;
          this.categories = categories;
          this.categories.push(-1);
          this.statuses.push(-1);
          this.letterForm.patchValue({ letterStatus: "" });
          this.letterForm.patchValue({ letterCategory: "" });
        },
        error: (error) => {
          console.error("Error fetching statuses or categories:", error);
        },
      });
    }
  }

  addTechnicalLetter(): void {
    this.isAdding = true;
    this.validateForm();
    if (this.validationErrors.length > 0) {
      this.isAdding = false;
      return;
    }
    this.addLetterFormData.set(
      "letterNumber",
      this.letterForm.value.letterNumber
    );
    this.addLetterFormData.set(
      "letterIssuerLkpId",
      this.letterForm.value.issuer
    );
    this.addLetterFormData.set(
      "letterStatus",
      this.letterForm.value.letterStatus
    );
    this.addLetterFormData.set(
      "letterCategory",
      this.letterForm.value.letterCategory
    );
    this.addLetterFormData.set(
      "letterIssueDate",
      new Date(new Date(this.letterForm.value.letterIssueDate).toUTCString())
        .toISOString()
        .split("T")[0]
    );
    this.addLetterFormData.set(
      "letterRecurringFlag",
      this.letterForm.value.letterRecurringFlag
    );
    this.addLetterFormData.set(
      "letterTitle",
      this.letterForm.value.letterTitle
    );
    this.addLetterFormData.set("ict", this.letterForm.value.ict);
    this.technicalLetterService
      .addTechnicalLetter(this.addLetterFormData)
      .subscribe({
        next: (response) => {
          this.addLetterFormData = new FormData();
          this.letterForm.reset();
          this.validationErrors = [];
          this.isAdding = false;
          this.router.navigate([
            `/technical-letters/edit-technical-letters/${response["id"]}`,
          ]);
        },
        error: (error) => {
          const detail = error?.error?.detail || error?.message || 'Unknown error';
          let errorMsg = `Failed to add letter: ${detail}`;
 
          const idMatch = detail.match(/\{"id":(\d+)\}/);
          if (idMatch && idMatch[1]) {
            const duplicateId = idMatch[1];
            errorMsg += ` <a href=\"/technical-letters/edit-technical-letters/${duplicateId}\">Go to existing letter</a>`;
          }
 
          this.validationErrors = [errorMsg];
          this.isAdding = false;
        }
      });
  }

  onFileSelected(event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.addLetterFormData.append("file", file);
    }
  }

  onCategoryChange(event: any): void {
    const selectedCategory = event.target.value;
    if (selectedCategory == -1) {
      this.openCategoryDialog();
    }
  }

  private openCategoryDialog(): void {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: "600px",
      disableClose: true,
      data: {
        inputField: "Category",
      },
    });

    dialogRef.afterClosed().subscribe((newCategory) => {
      if (newCategory) {
        this.categories.push(newCategory);
        this.letterForm.patchValue({ letterCategory: newCategory });
      }
    });
  }

  onStatusChange(event: any): void {
    const selectedStatus = event.target.value;
    if (selectedStatus == -1) {
      this.openStatusDialog();
    }
  }

  private openStatusDialog(): void {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: "600px",
      disableClose: true,
      data: {
        inputField: "Status",
      },
    });

    dialogRef.afterClosed().subscribe((newStatus) => {
      if (newStatus) {
        this.statuses.push(newStatus);
        this.letterForm.patchValue({ letterStatus: newStatus });
      }
    });
  }

  validateForm(): void {
    this.validationErrors = [];

    if (!this.letterForm.value.letterNumber) {
      this.validationErrors.push("Please specify Letter Number.");
    }
    if (!this.letterForm.value.letterStatus) {
      this.validationErrors.push("Please enter a valid Status.");
    }
    if (!this.letterForm.value.letterCategory) {
      this.validationErrors.push("Please enter a valid Category.");
    }
    if (!this.letterForm.value.letterIssueDate) {
      this.validationErrors.push("Please select a valid Issue Date.");
    }
    if (!this.letterForm.value.file) {
      this.validationErrors.push("Please upload a File.");
    }
    if (!this.letterForm.value.letterTitle) {
      this.validationErrors.push("Please specify Letter Title.");
    }
  }
}
